import { noop } from 'lodash';
import { type ComponentPropsWithoutRef, memo, type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { FileStatus } from '@air/feature-file-status/constants';
import {
  FileStatusPanelHeader,
  FileStatusTrackingPanelList,
  FileStatusTrackingPanelListItemAborted,
  FileStatusTrackingPanelListItemCompleted,
  FileStatusTrackingPanelListItemErrored,
  FileStatusTrackingPanelListItemPaused,
  FileStatusTrackingPanelListItemProgressing,
} from '@air/feature-file-status/ui';
import { UploadStatus } from '@air/redux-uploader';
import { cn } from '@air/utils-tailwind';

import { FileStatusTrackingPanelListItemStopped } from './FileStatusTrackingPanelListItemStopped';

const UPLOAD_PANE_WIDTH = 370;

export type FileTrackingItemCounts = {
  abortedCount: number;
  pausedCount: number;
  stoppedCount: number;
  erroredCount: number;
  completedCount: number;
  progressingCount: number;
};

export const progressingStatuses: UploadStatus[] = [
  UploadStatus.preparing,
  UploadStatus.uploading,
  UploadStatus.queued,
];

export type FileTrackingItemBase = {
  ext?: string;
  id: string;
  title: ReactNode;
  sizeInBytes?: number;
  thumbnailSource?: string;
  previewLoader?: ReactNode;
  avatar?: ReactNode;
  subtitle?: string;
  onRowClick?: () => void;
};

export type FileTrackingItem = FileTrackingItemBase & {
  currentBytesUploaded?: number;
  error?: string;
  uploadTimeRemaining?: number;
  status: UploadStatus;
  previewLoader?: ReactNode;
  disableCancel?: boolean;
  disableResume?: boolean;
  includeErrorInSummary: boolean;
};

export type FileStatusTrackingPanelProps = {
  onPauseAll?: () => void;
  onResumeAll?: () => void;
  onCancelAll?: () => void;
  onRetryAll?: () => void;
  onDismiss: () => void;
  onCancelItem?: (upload: FileTrackingItem) => void;
  onPauseItem?: (upload: FileTrackingItem) => void;
  onResumeItem?: (upload: FileTrackingItem) => void;
  onRetryItem?: (upload: FileTrackingItem) => void;
  items: FileTrackingItem[];
  testId?: string;
  /**
   * Always relying on default `false` as of this commit
   * @see https://github.com/AirLabsTeam/next/blob/1d878de1431c06b04be094791db396b29c514abc/providers/FileTrackingPaneStatusProvider.tsx#L21-L22
   * */
  isExpanded?: boolean;
  disableCancel?: boolean;
  disableDismiss?: boolean;
  disableToggle?: boolean;
  /**
   * Always relying on default `noop` as of this commit
   * @see https://github.com/AirLabsTeam/next/blob/1d878de1431c06b04be094791db396b29c514abc/providers/FileTrackingPaneStatusProvider.tsx#L21-L22
   */
  onExpandToggled?: (isExpanded: boolean) => void;
  getStatusTitle: (counts: FileTrackingItemCounts, status: FileStatus) => { title: string; subtitle?: string };
  getSummaryTrackingAvatar?: (counts: FileTrackingItemCounts, status: FileStatus) => ReactNode;
  getSummary?: (counts: FileTrackingItemCounts, status: FileStatus) => ReactNode;
  getCompletedItemsToDisplay?: (args: Pick<FileStatusTrackingPanelProps, 'items'>) => ReactNode[];
  getErroredItemsToDisplay?: (
    args: Pick<FileStatusTrackingPanelProps, 'items' | 'onCancelItem' | 'onRetryItem'>,
  ) => ReactNode[];
  getAbortedItemsToDisplay?: (args: Pick<FileStatusTrackingPanelProps, 'items'>) => ReactNode[];
  getStoppedItemsToDisplay?: (
    args: Pick<FileStatusTrackingPanelProps, 'items' | 'onCancelItem' | 'onResumeItem'>,
  ) => ReactNode[];
  getOngoingItemsToDisplay?: (
    args: Pick<FileStatusTrackingPanelProps, 'items' | 'onCancelItem' | 'onPauseItem' | 'onResumeItem'>,
  ) => ReactNode[];
} & Pick<ComponentPropsWithoutRef<'div'>, 'className' | 'style'>;

const getSummaryTrackingStatus = ({
  pausedCount,
  stoppedCount,
  erroredCount,
  progressingCount,
}: FileTrackingItemCounts): FileStatus => {
  if (stoppedCount) {
    return FileStatus.stopped;
  } else if (pausedCount > 0 && !progressingCount) {
    return FileStatus.paused;
  } else if (progressingCount) {
    return FileStatus.progressing;
  } else if (erroredCount) {
    return FileStatus.errored;
  } else {
    return FileStatus.completed;
  }
};

// right margin of uploader pane is 16, so for screens smaller than UPLOAD_PANE_WIDTH set window.innerWidth - 2*this margin
const UPLOADER_PANE_HORIZONTAL_MARGIN = 32;
const minWidth = typeof window === 'undefined' ? 0 : window.innerWidth - UPLOADER_PANE_HORIZONTAL_MARGIN;

export const FileStatusTrackingPanel = memo(
  ({
    className,
    disableCancel,
    disableDismiss,
    disableToggle,
    onCancelAll,
    onDismiss,
    onPauseAll,
    onResumeAll,
    onRetryAll,
    items,
    onCancelItem,
    onPauseItem,
    onResumeItem,
    onRetryItem,
    testId,
    isExpanded = false,
    onExpandToggled = noop,
    getStatusTitle,
    getSummary,
    getSummaryTrackingAvatar,
    getCompletedItemsToDisplay,
    getErroredItemsToDisplay,
    getAbortedItemsToDisplay,
    getStoppedItemsToDisplay,
    getOngoingItemsToDisplay,
    style,
  }: FileStatusTrackingPanelProps) => {
    /**
     * This effectively does the same thing as if `useState` initial state was set to `false`
     * Through to `toggleUploaderContent` and `setUploaderOpen` in the `useEffect`
     */
    const [isUploaderOpen, setUploaderOpen] = useState(isExpanded);

    const toggleUploaderContent = useCallback(() => {
      setUploaderOpen((isOpen) => {
        onExpandToggled?.(!isExpanded);
        return !isOpen;
      });
    }, [isExpanded, onExpandToggled]);

    useEffect(() => {
      setUploaderOpen(isExpanded);
    }, [isExpanded]);

    const completedItemsToDisplay = useMemo(() => {
      if (getCompletedItemsToDisplay) {
        return getCompletedItemsToDisplay({ items });
      }

      return items
        .filter((item) => item.status === UploadStatus.completed)
        .map((item) => <FileStatusTrackingPanelListItemCompleted item={item} key={item.id} />);
    }, [items, getCompletedItemsToDisplay]);

    const erroredItemsToDisplay = useMemo(() => {
      if (getErroredItemsToDisplay) {
        return getErroredItemsToDisplay({ items, onCancelItem, onRetryItem });
      }

      return items
        .filter((item) => item.status === UploadStatus.failed)
        .map((item) => (
          <FileStatusTrackingPanelListItemErrored
            error={item.error ?? ''}
            item={item}
            key={item.id}
            onCancel={onCancelItem}
            onRetry={onRetryItem}
          />
        ));
    }, [items, onCancelItem, onRetryItem, getErroredItemsToDisplay]);

    const abortedItemsToDisplay = useMemo(() => {
      if (getAbortedItemsToDisplay) {
        return getAbortedItemsToDisplay({ items });
      }

      return items
        .filter((item) => item.status === UploadStatus.aborted)
        .map((item) => <FileStatusTrackingPanelListItemAborted item={item} key={item.id} />);
    }, [items, getAbortedItemsToDisplay]);

    const stoppedItemsToDisplay = useMemo(() => {
      if (getStoppedItemsToDisplay) {
        return getStoppedItemsToDisplay({ items, onCancelItem, onResumeItem });
      }

      return items
        .filter((item) => item.status === UploadStatus.stopped)
        .map((item) => (
          <FileStatusTrackingPanelListItemStopped
            item={item}
            key={item.id}
            onCancel={onCancelItem}
            onResume={onResumeItem}
          />
        ));
    }, [items, onCancelItem, onResumeItem, getStoppedItemsToDisplay]);

    const ongoingItemsToDisplay = useMemo(() => {
      if (getOngoingItemsToDisplay) {
        return getOngoingItemsToDisplay({ items, onCancelItem, onPauseItem, onResumeItem });
      }

      return items
        .filter((item) => [...progressingStatuses, UploadStatus.paused].includes(item.status))
        .map((item) =>
          item.status === UploadStatus.paused ? (
            <FileStatusTrackingPanelListItemPaused
              item={item}
              key={item.id}
              onCancel={onCancelItem}
              onResume={onResumeItem}
            />
          ) : (
            <FileStatusTrackingPanelListItemProgressing
              item={item}
              key={item.id}
              onCancel={onCancelItem}
              onPause={onPauseItem}
            />
          ),
        );
    }, [onCancelItem, onPauseItem, onResumeItem, items, getOngoingItemsToDisplay]);

    const completedCount = completedItemsToDisplay.length;
    const abortedCount = abortedItemsToDisplay.length;
    const erroredCount = erroredItemsToDisplay.length;
    const activeErroredCount = items.filter(
      (item) => item.includeErrorInSummary && item.status === UploadStatus.failed,
    ).length;
    const pausedCount = items.filter((item) => item.status === UploadStatus.paused).length;
    const progressingCount = ongoingItemsToDisplay.length - pausedCount;
    const stoppedCount = stoppedItemsToDisplay.length;

    // If getSummaryTrackingStatus is provided, it will be used to determine the status
    // Otherwise, the default status will be used
    const status = getSummaryTrackingStatus({
      abortedCount,
      completedCount,
      erroredCount: activeErroredCount,
      pausedCount,
      progressingCount,
      stoppedCount,
    });

    const summary = getSummary?.(
      {
        abortedCount,
        completedCount,
        erroredCount: activeErroredCount,
        pausedCount,
        progressingCount,
        stoppedCount,
      },
      status,
    );

    const statusTitle = getStatusTitle(
      {
        abortedCount,
        completedCount,
        erroredCount,
        pausedCount,
        progressingCount,
        stoppedCount,
      },
      status,
    );

    const summaryTrackingAvatar = getSummaryTrackingAvatar?.(
      {
        abortedCount,
        completedCount,
        erroredCount: activeErroredCount,
        pausedCount,
        progressingCount,
        stoppedCount,
      },
      status,
    );

    return (
      <div
        className={cn(
          'rounded-8 ads:bg-base bg-gray-1 shadow-[0px_9px_24px_0px_#00000033,0px_3px_6px_0px_#0000001a,0px_0px_0px_1px_#0000000d]',
          className,
        )}
        data-testid={testId}
        style={{
          width: Math.min(minWidth, UPLOAD_PANE_WIDTH),
          ...style,
        }}
      >
        <FileStatusPanelHeader
          avatar={summaryTrackingAvatar}
          disableCancel={disableCancel}
          disableDismiss={disableDismiss}
          isUploaderOpen={isUploaderOpen}
          onCancel={onCancelAll}
          onDismiss={onDismiss}
          onPause={onPauseAll}
          onResume={onResumeAll}
          onToggle={disableToggle ? undefined : toggleUploaderContent}
          status={status}
          subtitle={statusTitle.subtitle}
          summary={summary}
          title={statusTitle.title}
        />

        <FileStatusTrackingPanelList
          abortedItems={abortedItemsToDisplay}
          completedItems={completedItemsToDisplay}
          erroredItems={erroredItemsToDisplay}
          isUploaderOpen={isUploaderOpen}
          ongoingItems={ongoingItemsToDisplay}
          onRetryAll={onRetryAll}
          stoppedItems={stoppedItemsToDisplay}
        />
      </div>
    );
  },
);

FileStatusTrackingPanel.displayName = 'FileStatusTrackingPanel';
